import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ChatBubbleOutline as ChatBubbleIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { ChatMessageDownloadButton, ExternalLinkButton, GenderAgeDisplay, NameNicknameDisplay, TimeDisplay } from '../common';
import { Nurse, NURSE_TYPE_THEME, ROLE_COLORS, ROLE_TYPES } from '../../types/nurse';
import { getExternalSeniorTalkRoomUrl } from '../../utils/urlUtils';
import { MESSAGE_DISPLAY_LIMIT } from '../../types/common';
import AssignSupportNurseDialog from './AssignSupportNurseDialog';

interface SupportNursesSectionProps {
  nurse: Nurse;
}

const SupportNursesSection: React.FC<SupportNursesSectionProps> = ({ nurse }) => {
  // サポート看護師登録ダイアログの状態
  const [ dialogOpen, setDialogOpen ] = useState(false);
  const [ isRefreshing, setIsRefreshing ] = useState(false);
  // ダイアログを開く
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };
  // ダイアログを閉じる
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  // 登録成功時の処理
  const handleSuccess = () => {
    // ページをリロードして最新の情報を取得
    setIsRefreshing(true);
    window.location.reload();
  };
  // support_nursesがない場合は空の配列を使用
  const supportNurses = nurse.support_nurses || [];
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
        boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6">サポート看護師</Typography>
                {supportNurses.length > 5 && (
                  <Chip
                    label={`${supportNurses.length}人`}
                    size="small"
                    variant="outlined"
                    sx={{
                      ml: 1,
                      fontWeight: 'bold',
                      color: NURSE_TYPE_THEME.NORMAL.DARK,
                      borderColor: NURSE_TYPE_THEME.NORMAL.DARK,
                      borderWidth: 1.5
                    }}
                  />
                )}
              </Box>
              {/* 一般看護師の場合のみ追加ボタンを表示 */}
              {(nurse.role_type === 'STAFF' || nurse.role_type === 'TALK_STAFF') && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<PersonAddIcon/>}
                  onClick={handleOpenDialog}
                  disabled={isRefreshing}
                  sx={{
                    borderRadius: '20px',
                    px: 2,
                    '&:hover': {
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}`,
                    }
                  }}
                >
                  追加
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }}/>

            {supportNurses.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                  サポート担当の看護師はいません
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '20px',
                    background: `linear-gradient(to top, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}CC, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}00)`,
                    pointerEvents: 'none',
                    zIndex: 1,
                    display: 'block'
                  }
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 1,
                    maxHeight: '500px', // 最大高さを指定
                    overflow: 'auto', // スクロール可能に
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}`,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: `${NURSE_TYPE_THEME.NORMAL.DARK}`
                      }
                    }
                  }}
                >
                  <Table
                    stickyHeader
                    aria-label="サポート看護師一覧"
                    sx={{
                      "& .MuiTableHead-root": {
                        position: "sticky",
                        top: 0,
                        zIndex: 2
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND} !important`,
                        borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          ロール
                        </TableCell>
                        <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          名前 / ニックネーム
                        </TableCell>
                        <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          性別 (年齢)
                        </TableCell>
                        <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          受付状態
                        </TableCell>
                        <TableCell width="27%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          最終メッセージ / 日時
                        </TableCell>
                        <TableCell width="13%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          チャット
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supportNurses
                      .map((supportNurse) => (
                        <TableRow
                          key={supportNurse.id}
                          hover
                          component={Link}
                          to={`/nurses/${supportNurse.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          {/* ロール */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: ROLE_COLORS[supportNurse.role_type as keyof typeof ROLE_COLORS] || '#757575',
                                fontSize: '0.75rem',
                                borderLeft: `3px solid ${ROLE_COLORS[supportNurse.role_type as keyof typeof ROLE_COLORS] || '#757575'}`,
                                pl: 1,
                                py: 0.3,
                                opacity: 0.85,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {ROLE_TYPES[supportNurse.role_type as keyof typeof ROLE_TYPES] || supportNurse.role_type}
                            </Typography>
                          </TableCell>

                          {/* 看護師名とニックネーム */}
                          <TableCell>
                            <NameNicknameDisplay
                              name={supportNurse.name}
                              nickname={supportNurse.nickname}
                              nameFontWeight="bold"
                              isTestAccount={(supportNurse as any)?.is_test_account || false}
                            />
                          </TableCell>

                          {/* 性別（年齢） */}
                          <TableCell>
                            <GenderAgeDisplay
                              genderType={supportNurse.gender_type || ''}
                              birthday={supportNurse.birthday || ''}
                              fontSize="0.9rem"
                            />
                          </TableCell>

                          {/* 受付状態 */}
                          <TableCell>
                            {supportNurse.can_assign !== undefined ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    color: supportNurse.can_assign ? '#2e7d32' : '#c62828',
                                    fontWeight: 'bold',
                                    fontSize: '1.2rem',
                                    mr: 0.5
                                  }}
                                >
                                  {supportNurse.can_assign ? '○' : '×'}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    color: supportNurse.can_assign ? '#2e7d32' : '#c62828',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {supportNurse.can_assign ? '受付中' : '停止中'}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                component="span"
                                sx={{
                                  color: '#757575',
                                  fontSize: '0.9rem',
                                  fontStyle: 'italic'
                                }}
                              >
                                情報なし
                              </Typography>
                            )}
                          </TableCell>

                          {/* 対話状態と最終チャット送信日時 */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {/* 最終チャットメッセージ */}
                              {supportNurse.last_chat_message ? (
                                supportNurse.last_chat_message.length > MESSAGE_DISPLAY_LIMIT ? (
                                  // 制限文字数を超える場合はTooltipを表示
                                  <Tooltip
                                    title={supportNurse.last_chat_message}
                                    arrow
                                    placement="top"
                                    enterDelay={500}
                                    sx={{ maxWidth: 500 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.85rem',
                                        color: 'text.primary',
                                        cursor: 'help',
                                        mb: 0.5
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {`${supportNurse.last_chat_message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`}
                                        <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                                      </Box>
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  // 30文字以内の場合はTooltipなし
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.85rem',
                                      color: 'text.primary',
                                      mb: 0.5
                                    }}
                                  >
                                    {supportNurse.last_chat_message}
                                  </Typography>
                                )
                              ) : (
                                // メッセージがない場合
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: 'text.secondary',
                                    fontStyle: 'italic',
                                    mb: 0.5
                                  }}
                                >
                                  メッセージのやり取りはまだありません
                                </Typography>
                              )}

                              {/* 最終チャット送信時間 */}
                              {supportNurse.last_chat_send_at && (
                                <TimeDisplay
                                  timestamp={supportNurse.last_chat_send_at}
                                  prefix="最終送信"
                                  fontSize="0.8rem"
                                  tooltipPlacement="left"
                                />
                              )}
                            </Box>
                          </TableCell>

                          {/* チャットリンク */}
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <ExternalLinkButton
                                href={getExternalSeniorTalkRoomUrl(supportNurse.senior_talk_room_id)}
                                label="開く"
                                tooltip="上席相談ルームを開く"
                                tooltipPlacement="left"
                                size="small"
                                buttonType="chat"
                                themeColor={{
                                  main: NURSE_TYPE_THEME.NORMAL.DARK,
                                  hover: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}99`
                                }}
                                onClick={(e) => { e.stopPropagation(); }}
                                sx={{ minWidth: 'auto', px: 1 }}
                              />
                              <div onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}>
                                <ChatMessageDownloadButton
                                  roomType="SENIOR_TALK_ROOM"
                                  roomId={supportNurse.senior_talk_room_id}
                                  size="small"
                                  variant="outlined"
                                  iconOnly={true}
                                  themeColor={{
                                    main: NURSE_TYPE_THEME.NORMAL.DARK,
                                    hover: NURSE_TYPE_THEME.NORMAL.BACKGROUND
                                  }}
                                />
                              </div>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>

      {/* サポート看護師登録ダイアログ */}
      <AssignSupportNurseDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        nurseId={nurse.id}
        onSuccess={handleSuccess}
        nurse={nurse}
      />
    </Card>
  );
};
export default SupportNursesSection;
